import { type ComponentType, Fragment, Suspense, useCallback, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useUiT } from '../i18n';
import { useLayerStack } from './useLayer';

/** Props every global layer component receives: its typed props and `close()`. */
export type GlobalLayerComponentProps<P> = { props: P; close: () => void };

/**
 * A global overlay (palette, composer, record peek …) opened from anywhere with `openLayer(id, props)`.
 * `component` should be `React.lazy(() => import(...))` so its code loads on first open.
 * `Effect` is an always-mounted *light* component (URL watchers, link interception); keep it tiny.
 */
export type GlobalLayerDefinition<M, K extends keyof M = keyof M> = {
  id: K;
  component: ComponentType<GlobalLayerComponentProps<M[K]>>;
  Effect?: ComponentType;
};

/** A definition for any id of the map (distributes over the ids, so each keeps its own props). */
export type AnyGlobalLayerDefinition<M> = { [K in keyof M]: GlobalLayerDefinition<M, K> }[keyof M];

type Instance = { key: string; id: PropertyKey; props: unknown };

/**
 * Shown while a global layer's code loads on its first open (a slow network): a small "Loading…" pill
 * at the top of the screen, above every layer. It fades in after 150 ms (CSS), so a fast open never
 * flashes it.
 */
export function LayerLoading() {
  const t = useUiT();
  const container = useLayerStack().container('system');
  const node = (
    <div className="layer-loading" role="status" data-testid="layer-loading">
      <span className="spinner" aria-hidden="true" />
      <span>{t('ui.loading')}…</span>
    </div>
  );
  return container ? createPortal(node, container) : node;
}

/** A layer that was asked for: its id says what its props are. */
export type GlobalLayerRequest<M> = { [K in keyof M]: { id: K; props: M[K] } }[keyof M];

export type GlobalLayerRegistry<M> = {
  /** Open (or update, when already open) a global layer. Unknown ids run the fallback. */
  open: <K extends keyof M>(id: K, props: M[K]) => void;
  /** Close by id. */
  close: (id: keyof M) => void;
  /** Open, or close when already open. */
  toggle: <K extends keyof M>(id: K, props: M[K]) => void;
  isOpen: (id: keyof M) => boolean;
  /** Register definitions (from the app's overlay modules). */
  define: (defs: Array<AnyGlobalLayerDefinition<M>>) => void;
  /** Register always-mounted light components that are not layers (URL watchers, link interception). */
  defineEffects: (effects: ComponentType[]) => void;
  /** What happens for ids nobody registered yet (navigate to the full page …). */
  setFallback: (fallback: (request: GlobalLayerRequest<M>) => void) => void;
  /** Mount once inside LayerRoot. */
  Host: ComponentType;
  /** Snapshot for tests. */
  openIds: () => Array<keyof M>;
};

/** Typed lazy registry behind `openLayer` (plan §4.1). One per app. */
export function createGlobalLayerRegistry<M>(): GlobalLayerRegistry<M> {
  let instances: Instance[] = [];
  let seq = 0;
  const defs = new Map<PropertyKey, AnyGlobalLayerDefinition<M>>();
  const listeners = new Set<() => void>();
  let fallback: ((request: GlobalLayerRequest<M>) => void) | null = null;
  const emit = () => {
    for (const l of [...listeners]) l();
  };
  const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  };

  const open = <K extends keyof M>(id: K, props: M[K]) => {
    if (!defs.has(id)) {
      // `id` and `props` are one request of the union above, a pairing a generic function cannot express.
      fallback?.({ id, props } as GlobalLayerRequest<M>);
      return;
    }
    const existing = instances.find((i) => i.id === id);
    if (existing) {
      instances = instances.map((i) => (i === existing ? { ...i, props } : i));
    } else {
      seq += 1;
      instances = [...instances, { key: `${String(id)}-${seq}`, id, props }];
    }
    emit();
  };
  const close = (id: keyof M) => {
    const next = instances.filter((i) => i.id !== id);
    if (next.length === instances.length) return;
    instances = next;
    emit();
  };
  const closeKey = (key: string) => {
    instances = instances.filter((i) => i.key !== key);
    emit();
  };

  function Host() {
    const current = useSyncExternalStore(
      subscribe,
      () => instances,
      () => instances,
    );
    const definitions = useSyncExternalStore(
      subscribe,
      () => defsSnapshot,
      () => defsSnapshot,
    );
    const effectList = useSyncExternalStore(
      subscribe,
      () => effects,
      () => effects,
    );
    const closer = useCallback((key: string) => () => closeKey(key), []);
    return (
      <>
        {definitions.map((d) => (d.Effect ? <d.Effect key={`effect-${String(d.id)}`} /> : null))}
        {effectList.map((Effect, i) => (
          <Effect key={`effect-${i}`} />
        ))}
        {current.map((instance) => {
          const def = defs.get(instance.id);
          if (!def) return null;
          const Component = def.component as ComponentType<GlobalLayerComponentProps<unknown>>;
          return (
            <Fragment key={instance.key}>
              <Suspense fallback={<LayerLoading />}>
                <Component props={instance.props} close={closer(instance.key)} />
              </Suspense>
            </Fragment>
          );
        })}
      </>
    );
  }
  let defsSnapshot: Array<AnyGlobalLayerDefinition<M>> = [];
  let effects: ComponentType[] = [];

  return {
    open,
    close,
    toggle: (id, props) => (instances.some((i) => i.id === id) ? close(id) : open(id, props)),
    isOpen: (id) => instances.some((i) => i.id === id),
    define: (list) => {
      for (const d of list) defs.set(d.id, d);
      defsSnapshot = [...defs.values()];
      emit();
    },
    defineEffects: (list) => {
      effects = [...effects, ...list.filter((e) => !effects.includes(e))];
      emit();
    },
    setFallback: (fn) => {
      fallback = fn;
    },
    Host,
    openIds: () => instances.map((i) => i.id as keyof M),
  };
}
